import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeaturesContext';
import { useTerms } from '../context/TerminologyContext';
import { getUser as getStoredUser } from '../utils/tenantStorage';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getTimeEntries,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  getEmployees,
  getCompanies,
  getAffiliates,
  getHolidays,
  getShiftSettings,
  getPTOCalendar,
  getTasks,
} from '../api';
import { useConfirmDialog } from './ConfirmDialog';
import { formatTimeDisplay, getUserTimezone } from '../utils/formatTime';
import { buildPlannedTaskParams } from '../utils/calendarTaskParams';
import { formatPay } from '../utils/formatUtils';

// Chip/text color per task urgency — same mapping Tasks.js and My Tasks use
// for the urgency chip, just expressed as theme color paths for sx.
const URGENCY_TEXT_COLORS = { low: 'text.secondary', normal: 'info.main', high: 'warning.main', urgent: 'error.main' };

// A task's planned_start as a time-of-day only ("9:00 AM") in the user's
// timezone — the date is already implied by which calendar day it's on.
const formatPlannedTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: getUserTimezone() });
  } catch {
    return '';
  }
};

// Helper: Format date as YYYY-MM-DD
const formatDateStr = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Helper: Get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Helper: Get day of week (0 = Sunday)
const getFirstDayOfMonth = (year, month) => {
  return new Date(year, month, 1).getDay();
};

// Month names
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Day names
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Server sends pto_type 'Out' + details_hidden for colleagues whose leave
// type the viewer may not see (GET /pto/calendar).
const ptoTypeLabel = (pto) => {
  if (pto.details_hidden || !pto.pto_type) return 'Out';
  return pto.pto_type.charAt(0).toUpperCase() + pto.pto_type.slice(1);
};
const PTO_HIDDEN_TIP = 'Type of time off is private: visible to the employee, their managers, admins, payroll admins and anyone with View all employees';

export default function CalendarView() {
  const { t } = useTerms();
  const { user, isAdmin, isProgramAdmin, isPayrollAdmin, isManager, hasPermission } = useAuth();
  // Same gating App.js uses to decide who gets the Tasks tab (vs. only My
  // Tasks) — those roles see every planned task in scope; everyone else
  // only sees their own.
  const canSeeTasksScreen = isAdmin || isProgramAdmin || isPayrollAdmin || (isManager && hasPermission('can_manage_tasks'));
  // Read once per render (not inside the useMemo below) so it's a real
  // dependency React can compare — a change in the account's timezone
  // preference re-groups plannedTasksByDate instead of leaving it stale.
  const timezone = getUserTimezone();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [ptoRequests, setPtoRequests] = useState([]);
  const [plannedTasks, setPlannedTasks] = useState([]);
  const [timeFormat, setTimeFormat] = useState('12h');
  const [error, setError] = useState('');
  const [success, showSuccess, clearSuccess] = useFeedback(3000);

  // Calendar state
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayEntries, setDayEntries] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [dialogHolidayNames, setDialogHolidayNames] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    employee_id: '',
    company_id: '',
    affiliate_id: '',
    entry_date: '',
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 0,
    hourly_rate: 25.00,
    description: '',
    work_details: '',
  });

  // Filter state (for admin/manager)
  const [filterEmployee, setFilterEmployee] = useState('');

  const canEdit = isAdmin || isManager;
  // The server reverts time fields for anyone without can_edit_entry_times.
  // This form is separate from TimeEntryFormDialog (which already gates them),
  // so without this an employee could type a new start time, save, and get the
  // old value handed back with no explanation — indistinguishable from a bug.
  const canEditTimes = isAdmin || user?.permissions?.can_edit_entry_times === true;

  // Tasks and Time off are tenant features from the feature registry
  // (FeaturesContext re-checks tasks whenever Tasks.js's toggle changes), so
  // the planned-tasks and PTO fetches below stay off for tenants without them.
  const { features } = useFeatures();
  const tasksFeatureEnabled = features.tasks;
  const timeOffEnabled = features.time_off;

  // Holiday advisory for the entry dialog — resolved for the selected
  // employee's schedule and date (which may be outside the loaded range)
  useEffect(() => {
    let cancelled = false;
    const date = formData.entry_date;
    if (!dialogOpen || !date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setDialogHolidayNames([]);
      return undefined;
    }
    const params = { start_date: date, end_date: date };
    if (formData.employee_id) params.employee_id = formData.employee_id;
    getHolidays(params)
      .then(res => { if (!cancelled) setDialogHolidayNames(res.data || []); })
      .catch(() => { if (!cancelled) setDialogHolidayNames([]); });
    return () => { cancelled = true; };
  }, [dialogOpen, formData.entry_date, formData.employee_id]);

  // Load entries for current view
  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on view
      let startDate, endDate;
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      if (viewMode === 'month') {
        // Get first day of calendar view (may include days from previous month)
        const firstDay = getFirstDayOfMonth(year, month);
        startDate = new Date(year, month, 1 - firstDay);
        // Get last day of calendar view (may include days from next month)
        const daysInMonth = getDaysInMonth(year, month);
        const lastDayOfMonth = new Date(year, month, daysInMonth);
        const remainingDays = 6 - lastDayOfMonth.getDay();
        endDate = new Date(year, month, daysInMonth + remainingDays);
      } else {
        // Week view
        const dayOfWeek = currentDate.getDay();
        startDate = new Date(year, month, currentDate.getDate() - dayOfWeek);
        endDate = new Date(year, month, currentDate.getDate() + (6 - dayOfWeek));
      }

      const params = {
        start_date: formatDateStr(startDate),
        end_date: formatDateStr(endDate),
      };
      
      if (filterEmployee) {
        params.employee_id = filterEmployee;
      }

      // Planned tasks follow the Employee filter too (assigned_employee_id),
      // on top of the viewer's own scope (everyone they may see on the Tasks
      // screen, or only their own via mine). Failing to load them (feature
      // off, an older server, a transient error) must never break the calendar.
      const plannedParams = buildPlannedTaskParams({
        startDate: params.start_date,
        endDate: params.end_date,
        canSeeTasksScreen,
        filterEmployee,
      });

      const [entriesRes, holidaysRes, ptoRes, plannedRes] = await Promise.all([
        getTimeEntries(params),
        getHolidays(params).catch(() => ({ data: [] })),
        timeOffEnabled ? getPTOCalendar(params).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        tasksFeatureEnabled ? getTasks(plannedParams).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setEntries(entriesRes.data || []);
      setHolidays(holidaysRes.data || []);
      setPtoRequests(ptoRes.data || []);
      const plannedList = Array.isArray(plannedRes.data) ? plannedRes.data : (plannedRes.data?.tasks || []);
      setPlannedTasks(plannedList.filter((pt) => pt.planned_start));
    } catch (err) {
      console.error('Error loading entries:', err);
      setError('Failed to load calendar entries');
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, filterEmployee, tasksFeatureEnabled, timeOffEnabled, canSeeTasksScreen]);

  // Load reference data
  const loadReferenceData = useCallback(async () => {
    try {
      const [empRes, compRes, affRes, shiftRes] = await Promise.all([
        getEmployees(),
        getCompanies(),
        getAffiliates(),
        getShiftSettings().catch(() => ({ data: { time_format: '12h' } })),
      ]);
      setEmployees((empRes.data || []).filter(e => e.is_active !== false));
      setCompanies(compRes.data || []);
      setAffiliates(affRes.data || []);
      if (shiftRes.data?.time_format) {
        setTimeFormat(shiftRes.data.time_format);
      }

      // Set default employee
      const user = getStoredUser() || {};
      if (user.employeeId) {
        setFormData(prev => ({ ...prev, employee_id: user.employeeId }));
        if (!isAdmin && !isManager) {
          setFilterEmployee(user.employeeId);
        }
      }
    } catch (err) {
      console.error('Error loading reference data:', err);
    }
  }, [isAdmin, isManager]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Navigation handlers
  const goToToday = () => setCurrentDate(new Date());
  
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  // Get entries for a specific date
  const getEntriesForDate = (dateStr) => {
    return entries.filter(entry => {
      const entryDate = entry.entry_date.split('T')[0];
      return entryDate === dateStr;
    });
  };

  // Get holidays for a specific date
  const getHolidaysForDate = (dateStr) => {
    return holidays.filter(h => {
      const hDate = h.holiday_date.split('T')[0];
      return hDate === dateStr;
    });
  };

  // Get PTO requests that cover a specific date
  const getPTOForDate = (dateStr) => {
    return ptoRequests.filter(pto => {
      const start = pto.start_date.split('T')[0];
      const end = pto.end_date.split('T')[0];
      return dateStr >= start && dateStr <= end;
    });
  };

  // Planned tasks grouped by the calendar date they land on, resolved in the
  // user's timezone (planned_start is UTC) — 'en-CA' formats as YYYY-MM-DD,
  // which lines up with dateStr without any manual offset math. Precomputed
  // once per plannedTasks/timezone change rather than re-filtering the whole
  // list (with an Intl call per task) for every one of the ~42 day cells a
  // month view renders.
  const plannedTasksByDate = useMemo(() => {
    const map = new Map();
    plannedTasks.forEach((task) => {
      try {
        const dateStr = new Date(task.planned_start).toLocaleDateString('en-CA', { timeZone: timezone });
        if (!map.has(dateStr)) map.set(dateStr, []);
        map.get(dateStr).push(task);
      } catch {
        /* an unparsable planned_start just doesn't show up on the calendar */
      }
    });
    return map;
  }, [plannedTasks, timezone]);

  const getPlannedTasksForDate = (dateStr) => plannedTasksByDate.get(dateStr) || [];

  // Handle date click
  const handleDateClick = (dateStr) => {
    const dateEntries = getEntriesForDate(dateStr);
    setSelectedDate(dateStr);
    setDayEntries(dateEntries);
    setDayDialogOpen(true);
  };

  // Handle add entry
  const handleAddEntry = (dateStr = null) => {
    const targetDate = dateStr || selectedDate || formatDateStr(new Date());
    setFormData({
      employee_id: formData.employee_id || '',
      company_id: '',
      affiliate_id: '',
      entry_date: targetDate,
      start_time: '09:00',
      end_time: '17:00',
      break_minutes: 0,
      hourly_rate: employees.find(e => e.id === formData.employee_id)?.hourly_rate || 25.00,
      description: '',
      work_details: '',
    });
    setEditMode(false);
    setCurrentEntry(null);
    setDialogOpen(true);
    setDayDialogOpen(false);
  };

  // Handle edit entry
  const handleEditEntry = (entry) => {
    setFormData({
      employee_id: entry.employee_id,
      company_id: entry.company_id || '',
      affiliate_id: entry.affiliate_id || '',
      entry_date: entry.entry_date.split('T')[0],
      start_time: entry.start_time,
      end_time: entry.end_time,
      break_minutes: entry.break_minutes || 0,
      hourly_rate: entry.hourly_rate,
      description: entry.description || '',
      work_details: entry.work_details || '',
    });
    setEditMode(true);
    setCurrentEntry(entry);
    setDialogOpen(true);
    setDayDialogOpen(false);
  };

  // Handle delete entry
  const handleDeleteEntry = async (entry) => {
    const confirmed = await showConfirm({
      title: 'Delete Entry',
      message: 'Are you sure you want to delete this time entry?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      await deleteTimeEntry(entry.id);
      showSuccess('Entry deleted successfully');
      loadEntries();
      setDayDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!formData.employee_id || !formData.company_id) {
      setError(`Please select an employee and ${t('client.singular', { lower: true })}`);
      return;
    }

    try {
      if (editMode && currentEntry) {
        await updateTimeEntry(currentEntry.id, formData);
        showSuccess('Entry updated successfully');
      } else {
        await createTimeEntry(formData);
        showSuccess('Entry created successfully');
      }
      setDialogOpen(false);
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save entry');
    }
  };

  // Handle employee change in form
  const handleEmployeeChange = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    setFormData(prev => ({
      ...prev,
      employee_id: employeeId,
      hourly_rate: employee?.hourly_rate || prev.hourly_rate,
    }));
  };

  // Render calendar grid
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = formatDateStr(new Date());

    const days = [];
    
    // Previous month days
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
    
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const dateStr = formatDateStr(new Date(prevYear, prevMonth, day));
      days.push({ day, dateStr, isCurrentMonth: false });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateStr(new Date(year, month, day));
      days.push({ day, dateStr, isCurrentMonth: true });
    }

    // Next month days
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    
    for (let day = 1; day <= remainingDays; day++) {
      const dateStr = formatDateStr(new Date(nextYear, nextMonth, day));
      days.push({ day, dateStr, isCurrentMonth: false });
    }

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {/* Day headers */}
        {DAYS.map(day => (
          <Box
            key={day}
            sx={{
              p: 1,
              textAlign: 'center',
              fontWeight: 'bold',
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 1,
            }}
          >
            {day}
          </Box>
        ))}

        {/* Calendar days */}
        {days.map(({ day, dateStr, isCurrentMonth }, idx) => {
          const dayEntries = getEntriesForDate(dateStr);
          const dayHolidays = getHolidaysForDate(dateStr);
          const dayPTO = getPTOForDate(dateStr);
          const dayPlanned = getPlannedTasksForDate(dateStr);
          const isToday = dateStr === today;
          const isHoliday = dayHolidays.length > 0;
          const hasPTO = dayPTO.length > 0;
          const totalHours = dayEntries.reduce((sum, e) => sum + parseFloat(e.hours_worked || 0), 0);
          const hasPaid = dayEntries.some(e => e.is_paid);
          const hasUnpaid = dayEntries.some(e => !e.is_paid);

          return (
            <Paper
              key={idx}
              elevation={isToday ? 3 : 1}
              onClick={() => handleDateClick(dateStr)}
              sx={{
                p: 0.5,
                minHeight: 80,
                cursor: 'pointer',
                bgcolor: isToday ? 'primary.light' : isHoliday ? '#FFF3E0' : hasPTO ? '#E8F5E9' : isCurrentMonth ? 'background.paper' : 'action.hover',
                opacity: isCurrentMonth ? 1 : 0.6,
                border: isToday ? '2px solid' : isHoliday ? '1px solid #FFB74D' : 'none',
                borderColor: isToday ? 'primary.main' : '#FFB74D',
                '&:hover': {
                  bgcolor: isToday ? 'primary.light' : isHoliday ? '#FFE0B2' : 'action.selected',
                },
                transition: 'background-color 0.2s',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isToday ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {day}
              </Typography>

              {isHoliday && (
                <Box sx={{
                  bgcolor: '#E65100',
                  color: '#fff',
                  borderRadius: 0.5,
                  px: 0.5,
                  py: 0.25,
                  mt: 0.5,
                  textAlign: 'center',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1.2 }}>
                    {dayHolidays[0].name}
                  </Typography>
                </Box>
              )}

              {dayEntries.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                    {totalHours.toFixed(1)}h
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                    {hasPaid && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                    )}
                    {hasUnpaid && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                    )}
                  </Box>
                  {dayEntries.length > 2 && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayEntries.length - 2} more
                    </Typography>
                  )}
                </Box>
              )}

              {hasPTO && dayPTO.map((pto, pi) => {
                const isPending = pto.status === 'pending';
                // The server hides the leave type (details_hidden) for colleagues the viewer
                // doesn't manage; those show only "Out" and no pending marker.
                const showDetails = !pto.details_hidden;
                const displayText = `${pto.first_name} ${pto.last_name?.charAt(0)}. — ${ptoTypeLabel(pto)}${isPending && showDetails ? ' (pending)' : ''}`;

                const ptoBox = (
                  <Box key={pi} sx={{
                    bgcolor: isPending && showDetails ? 'transparent' : '#7B1FA2',
                    color: isPending && showDetails ? '#7B1FA2' : '#fff',
                    border: isPending && showDetails ? '1px dashed #7B1FA2' : 'none',
                    borderRadius: 0.5,
                    px: 0.5, py: 0.25, mt: 0.25, textAlign: 'center',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.55rem', lineHeight: 1.2 }}>
                      {displayText}
                    </Typography>
                  </Box>
                );
                return pto.details_hidden
                  ? <Tooltip key={pi} title={PTO_HIDDEN_TIP}>{ptoBox}</Tooltip>
                  : ptoBox;
              })}

              {dayPlanned.length > 0 && (
                <Box sx={{ mt: 0.25 }}>
                  {dayPlanned.slice(0, 2).map((task) => (
                    <Tooltip
                      key={task.id}
                      title={`${task.affiliate_name || task.company_name || 'No property'} — ${task.status}${task.is_late ? ' (late)' : ''}`}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: '0.65rem',
                          lineHeight: 1.3,
                          color: URGENCY_TEXT_COLORS[task.priority] || 'text.primary',
                          textDecoration: task.status === 'complete' ? 'line-through' : 'none',
                          fontWeight: task.is_late ? 700 : 400,
                          fontStyle: task.is_late ? 'italic' : 'normal',
                        }}
                      >
                        {formatPlannedTime(task.planned_start)} · {task.title}
                      </Typography>
                    </Tooltip>
                  ))}
                  {dayPlanned.length > 2 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      +{dayPlanned.length - 2} more
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderWeekView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = currentDate.getDate();
    const dayOfWeek = currentDate.getDay();
    const today = formatDateStr(new Date());

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(year, month, date - dayOfWeek + i);
      days.push({
        day: d.getDate(),
        dateStr: formatDateStr(d),
        dayName: DAYS[i],
        monthName: MONTHS[d.getMonth()].slice(0, 3),
      });
    }

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {days.map(({ day, dateStr, dayName, monthName }) => {
          const dayEntries = getEntriesForDate(dateStr);
          const dayHolidays = getHolidaysForDate(dateStr);
          const dayPTO = getPTOForDate(dateStr);
          const dayPlanned = getPlannedTasksForDate(dateStr);
          const isToday = dateStr === today;
          const isHoliday = dayHolidays.length > 0;
          const hasPTO = dayPTO.length > 0;

          return (
            <Paper
              key={dateStr}
              elevation={isToday ? 3 : 1}
              sx={{
                p: 1,
                minHeight: 200,
                cursor: 'pointer',
                bgcolor: isToday ? 'primary.light' : isHoliday ? '#FFF3E0' : hasPTO ? '#E8F5E9' : 'background.paper',
                border: isToday ? '2px solid' : isHoliday ? '1px solid #FFB74D' : 'none',
                borderColor: isToday ? 'primary.main' : '#FFB74D',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  pb: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {dayName}
                </Typography>
                <Typography variant="body2">
                  {monthName} {day}
                </Typography>
              </Box>
              {isHoliday && (
                <Box sx={{
                  bgcolor: '#E65100',
                  color: '#fff',
                  borderRadius: 0.5,
                  px: 1,
                  py: 0.25,
                  mb: 0.5,
                  textAlign: 'center',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', lineHeight: 1.2 }}>
                    {dayHolidays[0].name}
                  </Typography>
                </Box>
              )}

              {dayEntries.map((entry, idx) => (
                <Tooltip
                  key={entry.id}
                  title={`${entry.first_name} ${entry.last_name} - ${entry.company_name}`}
                >
                  <Chip
                    size="small"
                    label={`${parseFloat(entry.hours_worked || 0).toFixed(1)}h`}
                    color={entry.is_paid ? 'success' : 'warning'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEntry(entry);
                    }}
                    sx={{ mb: 0.5, mr: 0.5, cursor: 'pointer' }}
                  />
                </Tooltip>
              ))}

              {dayPTO.map((pto, pi) => {
                const ptoChip = (
                  <Chip
                    key={`pto-${pi}`}
                    size="small"
                    label={`${pto.first_name} ${pto.last_name?.charAt(0) || ''} — ${ptoTypeLabel(pto)} (${parseFloat(pto.hours_per_day).toFixed(1)}h)`}
                    sx={{ mb: 0.5, mr: 0.5, bgcolor: '#7B1FA2', color: '#fff', fontSize: '0.7rem' }}
                  />
                );
                return pto.details_hidden
                  ? <Tooltip key={`pto-${pi}`} title={PTO_HIDDEN_TIP}>{ptoChip}</Tooltip>
                  : ptoChip;
              })}

              {dayPlanned.map((task) => (
                <Tooltip
                  key={`planned-${task.id}`}
                  title={`${task.affiliate_name || task.company_name || 'No property'} — ${task.status}${task.is_late ? ' (late)' : ''}`}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${formatPlannedTime(task.planned_start)} · ${task.title}`}
                    sx={{
                      mb: 0.5,
                      mr: 0.5,
                      fontSize: '0.7rem',
                      color: URGENCY_TEXT_COLORS[task.priority] || 'text.primary',
                      textDecoration: task.status === 'complete' ? 'line-through' : 'none',
                      borderColor: task.is_late ? 'error.main' : undefined,
                      borderWidth: task.is_late ? 2 : 1,
                      fontWeight: task.is_late ? 700 : 400,
                    }}
                  />
                </Tooltip>
              ))}

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddEntry(dateStr);
                }}
                sx={{ mt: 1 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Paper>
          );
        })}
      </Box>
    );
  };

  if (loading && entries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}

      {/* Header */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        {/* Mobile Layout */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {/* Row 1: Month/Year and Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h6" fontWeight="bold">
              {MONTHS[currentDate.getMonth()].slice(0, 3)} {currentDate.getFullYear()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={goToPrevious}>
                <PrevIcon />
              </IconButton>
              <IconButton size="small" onClick={goToToday}>
                <TodayIcon />
              </IconButton>
              <IconButton size="small" onClick={goToNext}>
                <NextIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Row 2: View toggle and Add button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <ButtonGroup size="small">
              <Button
                variant={viewMode === 'month' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('month')}
              >
                Month
              </Button>
              <Button
                variant={viewMode === 'week' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
            </ButtonGroup>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleAddEntry()}
            >
              Add
            </Button>
          </Box>

          {/* Row 3: Employee filter (admin/manager only) */}
          {(isAdmin || isManager) && (
            <TextField
              select
              size="small"
              label="Employee"
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        {/* Desktop Layout */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={goToPrevious}>
                  <PrevIcon />
                </IconButton>
                <Button onClick={goToToday} startIcon={<TodayIcon />} variant="outlined" size="small">
                  Today
                </Button>
                <IconButton onClick={goToNext}>
                  <NextIcon />
                </IconButton>
                <Typography variant="h6" sx={{ ml: 2 }}>
                  {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                </Typography>
              </Box>
            </Grid>

            <Grid item md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <ButtonGroup>
                  <Button
                    variant={viewMode === 'month' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('month')}
                  >
                    Month
                  </Button>
                  <Button
                    variant={viewMode === 'week' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('week')}
                  >
                    Week
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>

            <Grid item md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {(isAdmin || isManager) && (
                  <TextField
                    select
                    size="small"
                    label="Employee"
                    value={filterEmployee}
                    onChange={(e) => setFilterEmployee(e.target.value)}
                    sx={{ minWidth: 150 }}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">All Employees</MenuItem>
                    {employees.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddEntry()}
                >
                  Add Entry
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="caption">Paid</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
            <Typography variant="caption">Unpaid</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 24, height: 12, borderRadius: 0.5, bgcolor: '#E65100' }} />
            <Typography variant="caption">Holiday</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Calendar */}
      <Paper sx={{ p: 2 }}>
        {viewMode === 'month' ? renderMonthView() : renderWeekView()}
      </Paper>

      {/* Day Detail Dialog */}
      <Dialog open={dayDialogOpen} onClose={() => setDayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </DialogTitle>
        <DialogContent>
          {selectedDate && getHolidaysForDate(selectedDate).length > 0 && (
            <Alert severity="info" icon={false} sx={{ mb: 2, bgcolor: '#FFF3E0', border: '1px solid #FFB74D' }}>
              <Typography variant="subtitle2" sx={{ color: '#E65100' }}>
                {getHolidaysForDate(selectedDate).map(h => h.name).join(', ')}
              </Typography>
            </Alert>
          )}
          {selectedDate && getPTOForDate(selectedDate).length > 0 && (
            <Box sx={{ mb: 2 }}>
              {getPTOForDate(selectedDate).map((pto, i) => (
                <Alert key={i} severity="success" icon={false}
                  sx={{ mb: 0.5, bgcolor: '#F3E5F5', border: '1px solid #CE93D8' }}>
                  <Typography variant="subtitle2" sx={{ color: '#7B1FA2' }}>
                    {pto.first_name} {pto.last_name} — {ptoTypeLabel(pto)} ({parseFloat(pto.hours_per_day).toFixed(1)}h)
                    {pto.details_hidden && (
                      <Typography component="span" variant="caption"> · type private</Typography>
                    )}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}
          {dayEntries.length > 0 ? (
            <List>
              {dayEntries.map((entry, idx) => (
                <React.Fragment key={entry.id}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      canEdit && (
                        <Box>
                          <IconButton onClick={() => handleEditEntry(entry)} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteEntry(entry)} size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {entry.first_name} {entry.last_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={entry.is_paid ? 'Paid' : 'Unpaid'}
                            color={entry.is_paid ? 'success' : 'warning'}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span">
                            {entry.company_name} | {formatTimeDisplay(entry.start_time, timeFormat)} - {formatTimeDisplay(entry.end_time, timeFormat)}
                          </Typography>
                          <br />
                          <Typography variant="body2" component="span">
                            {entry.pay_hidden
                              ? `${parseFloat(entry.hours_worked || 0).toFixed(2)} hrs`
                              : `${parseFloat(entry.hours_worked || 0).toFixed(2)} hrs @ ${formatPay(entry.hourly_rate)}/hr = ${formatPay(entry.total_pay)}`}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No entries for this day
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddEntry()}
          >
            Add Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Entry Form Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Time Entry' : 'Add Time Entry'}</DialogTitle>
        <DialogContent>
          {editMode && !canEditTimes && (
            <Alert severity="info" sx={{ mt: 1 }}>
              The times on this entry come from your clock in and out and cannot be
              edited. You can still update the description and work details — ask a
              manager to correct the times.
            </Alert>
          )}
          {dialogHolidayNames.length > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This date is a holiday: <strong>{dialogHolidayNames.map(h => h.name).join(', ')}</strong>
              {dialogHolidayNames[0].worked_holiday_multiplier > 1 && formData.employee_id && (
                <> — a <strong>×{Number(dialogHolidayNames[0].worked_holiday_multiplier).toFixed(2)}</strong> holiday pay rate will be applied on save</>
              )}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Employee"
                value={formData.employee_id}
                onChange={(e) => handleEmployeeChange(e.target.value)}
                required
              >
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label={t('client.singular')}
                value={formData.company_id}
                onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                required
              >
                {companies.map(comp => (
                  <MenuItem key={comp.id} value={comp.id}>
                    {comp.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label={`${t('affiliate.singular')} (Optional)`}
                value={formData.affiliate_id}
                onChange={(e) => setFormData(prev => ({ ...prev, affiliate_id: e.target.value }))}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">None</MenuItem>
                {affiliates
                  .filter(aff =>
                    !formData.company_id ||
                    String(aff.company_id) === String(formData.company_id)
                  )
                  .map(aff => (
                    <MenuItem key={aff.id} value={aff.id}>
                      {aff.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.entry_date}
                onChange={(e) => setFormData(prev => ({ ...prev, entry_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                disabled={Boolean(currentEntry) && !canEditTimes}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                disabled={Boolean(currentEntry) && !canEditTimes}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                disabled={Boolean(currentEntry) && !canEditTimes}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Break (minutes)"
                type="number"
                value={formData.break_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, break_minutes: parseInt(e.target.value, 10) || 0 }))}
                inputProps={{ min: 0 }}
                disabled={Boolean(currentEntry) && !canEditTimes}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Hourly Rate"
                type="number"
                value={formData.hourly_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                placeholder={`Short summary for the ${t('client.singular', { lower: true })} invoice`}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Work Details"
                placeholder="Internal notes (not on invoice)"
                multiline
                rows={2}
                value={formData.work_details}
                onChange={(e) => setFormData(prev => ({ ...prev, work_details: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
